// src/data/signoff.js
//
// THE PER-PERSON SIGNATURE SIGN-OFF - Bob, 2026-09-25: "I'd like to offer the General Chairmen the
// opportunity to create their own letterhead and signature sign-off ... the same exact treatment -
// accurate, no lies." Ruling 230 (letterhead's sibling) and RULING 231, both under PD-19, withdrawable
// by number.
//
// ================================================================================================
// THE PRINCIPLE THIS MODULE EXISTS TO ENFORCE, IN BOB'S FRAME:
//
//   A SIGNATURE IS AN ATTESTATION, AND AN UNAUTHORIZED ONE IS A LIE.
//
// A generated letter is a DRAFT under the officer's letterhead - a preview. The signature is affixed
// ONLY when that officer signs off on THAT SPECIFIC letter. It is never baked in by a generator, never
// applied to a letter he did not personally adopt, and never an image applied automatically. This is
// the "no lies" standard (PD-0, PD-5) applied to the officer's own name.
// ================================================================================================
//
// HOW THE ARCHITECTURE HOLDS THE LINE, not just this file:
//   1. The letter generators (letterGroup.js, disciplineLetters.js, onboardingLetters.js) produce a
//      draft and call markDraft() on it. They do NOT import affixSignoff and cannot sign anything.
//   2. affixSignoff() is called ONLY by a panel, in response to the officer clicking sign-off on one
//      letter. It fails closed: with no name it returns ok:false and the text unchanged - it never
//      invents a name to sign with (PD-0: the absence of a field is never a value).
//   3. An uploaded signature IMAGE is a forgeable asset. Its bytes live in Storage scoped to the
//      officer alone (signatures/{uid}, storage.rules). This module only ever carries a POINTER to it
//      and only affixes it when the officer explicitly chose to on this letter (useImage). A text copy
//      says an image was affixed; it does not pretend to reproduce it.
//
// Self-scope, like the profile/office model: each officer controls only his own sign-off. Admin does
// not silently set a signature for someone - a signature is personal (users/{uid}.signoff, self-write).
//
// Pure module: no React, no Firestore.

const F = Object.freeze;

function isStr(v) { return typeof v === 'string'; }
function nonEmpty(v) { return isStr(v) ? v.trim() !== '' : v !== null && v !== undefined && v !== ''; }
function clean(v) { return isStr(v) ? v.replace(/[\r\n]+/g, ' ').trim() : ''; }
function dayOf(v) {
  if (!v) return '';
  const s = isStr(v) ? v : (v instanceof Date ? v.toISOString() : (typeof v.toISOString === 'function' ? v.toISOString() : String(v)));
  return s.slice(0, 10);
}

export const MAX_FIELD_LEN = 140;

// The unmistakable mark on every preview. A copied or downloaded draft carries this so it can never be
// mistaken for a signed letter - the safety principle applied to the bytes, not only the screen.
export const DRAFT_NOTICE =
  '--- DRAFT - NOT SIGNED ---\n'
  + 'This is a preview under the officer\'s letterhead. It carries no signature and adopts nothing. It\n'
  + 'becomes a signed letter only when the officer signs off on THIS letter.';

// A signed letter ends here. Split out as a constant so a check can prove a draft never contains it and
// an adopted letter always does.
export const SIGNED_MARK = '--- SIGNED ---';

export function isDraftMarked(text) { return isStr(text) && text.indexOf(DRAFT_NOTICE) !== -1; }
export function isSigned(text) { return isStr(text) && text.indexOf(SIGNED_MARK) !== -1; }

// Append the draft notice to a generated letter body, once. Idempotent.
export function markDraft(text) {
  const t = isStr(text) ? text : '';
  if (isDraftMarked(t)) return t;
  return t + '\n\n' + DRAFT_NOTICE;
}

// Remove the draft notice - used at the moment of sign-off, so an adopted letter does not carry it.
export function stripDraft(text) {
  if (!isStr(text)) return '';
  return text.split('\n\n' + DRAFT_NOTICE).join('').split(DRAFT_NOTICE).join('').replace(/\s+$/,'');
}

// The stored per-person sign-off. `typedName` defaults to the officer's own name and is what the "/s/"
// line reads; `typedTitle` and `typedCommittee` are optional and complete Bob's example
// ("/s/ Christian Eckert, General Chairman, GO-342"). `imagePath` is an OPTIONAL Storage pointer to the
// officer's own signature image - never bytes, never another person's. `recordedBy`/`recordedAt` are the
// provenance the self-write rule requires.
export function normalizeSignoff(input) {
  const i = input || {};
  const out = {
    typedName: clean(i.typedName).slice(0, MAX_FIELD_LEN),
    typedTitle: clean(i.typedTitle).slice(0, MAX_FIELD_LEN),
    typedCommittee: clean(i.typedCommittee).slice(0, MAX_FIELD_LEN),
    imagePath: nonEmpty(i.imagePath) ? clean(i.imagePath) : null,
  };
  if (nonEmpty(i.recordedBy)) out.recordedBy = clean(i.recordedBy);
  if (nonEmpty(i.recordedAt)) out.recordedAt = String(i.recordedAt).trim();
  return out;
}

// A person has a usable sign-off when there is a name to sign with. An image alone is NOT a sign-off:
// a picture with no typed name attests nothing legible, and the typed sign-off is the safe default.
export function hasSignoff(s) { return !!(s && nonEmpty(s.typedName)); }

export function validateSignoff(input) {
  const value = normalizeSignoff(input);
  const errors = [];
  if (!nonEmpty(value.typedName)) errors.push('a sign-off carries the name it signs with - the typed sign-off is the safe default');
  return { ok: errors.length === 0, errors, value };
}

// The "/s/ ..." line, built from the parts present. Name is required; a line with no name is '' (never
// a "/s/ ," that signs with nobody). Title and committee append only when present.
export function typedSignoffLine(signoff) {
  const s = normalizeSignoff(signoff);
  if (!nonEmpty(s.typedName)) return '';
  const parts = [s.typedName];
  if (nonEmpty(s.typedTitle)) parts.push(s.typedTitle);
  if (nonEmpty(s.typedCommittee)) parts.push(s.typedCommittee);
  return '/s/ ' + parts.join(', ');
}

// THE ACT. Affix the officer's sign-off to ONE draft letter, at his explicit instruction. Returns the
// adopted text and the signature metadata a panel needs to render the image in the on-screen copy.
//
// Fails closed (ok:false, text returned UNCHANGED and still a draft) when there is no name to sign
// with - it never manufactures a signature (PD-0). `useImage` affixes the officer's image ONLY when he
// asked for it AND one is on file; the text copy records that an image was affixed without pretending
// to reproduce it (a text file holds no image).
export function affixSignoff(draftText, signoff, opts) {
  const o = opts || {};
  const v = validateSignoff(signoff);
  if (!v.ok) {
    return { ok: false, missing: v.errors, text: isStr(draftText) ? draftText : '', adopted: false,
      signedName: null, signedAt: null, usedImage: false, imagePath: null };
  }
  const s = v.value;
  const at = dayOf(o.at || new Date());
  const useImage = !!o.useImage && nonEmpty(s.imagePath);
  const body = stripDraft(isStr(draftText) ? draftText : '');
  const block = [];
  block.push(SIGNED_MARK);
  if (useImage) {
    block.push('[The officer affixed his signature image to the signed letter. '
      + 'The image is on file and is not reproduced in this text copy.]');
  }
  block.push(typedSignoffLine(s));
  block.push('Signed ' + at);
  const text = body + '\n\n' + block.join('\n');
  return {
    ok: true, missing: [], text, adopted: true,
    signedName: s.typedName, signedAt: at,
    usedImage: useImage, imagePath: useImage ? s.imagePath : null,
  };
}

// Who may set a person's sign-off: THAT PERSON, and nobody else. A signature is personal - the
// administrator does not set one for someone (Bob's principle, and the reason the firestore rule's
// dedicated signoff clause is self-only). `uid` is the profile being edited; `user` is the caller.
export function mayRecordSignoff(user, uid) {
  return !!(user && user.uid && uid && user.uid === uid);
}

// The patch a person writes to his own sign-off. Returns { ok, errors, patch } - patch is
// { signoff: {...} } and nothing else, recordedBy his own uid, which the self-write rule asserts.
export function signoffRecord(user, input, at) {
  const errors = [];
  if (!user || !user.uid) errors.push('no signed-in account');
  const v = validateSignoff(input);
  errors.push(...v.errors);
  if (errors.length) return { ok: false, errors, patch: null };
  return {
    ok: true, errors: [],
    patch: {
      signoff: {
        ...v.value,
        recordedBy: user.uid,
        recordedByName: (user.name || user.email) || 'Unknown',
        recordedAt: at || new Date().toISOString(),
      },
    },
  };
}

export const SIGNATURE_IMAGE = F({
  // The Storage home of an officer's signature image, scoped to him alone (storage.rules).
  pathFor: (uid, fileName) => 'signatures/' + String(uid) + '/' + String(fileName || 'signature.png'),
  MAX_BYTES: 2 * 1024 * 1024,
  CONTENT_TYPES: F(['image/png', 'image/jpeg', 'image/jpg']),
});
